import React, { useState } from 'react';
import { useAppSelector } from '~/hooks';
import { firePermanentHireRequestSubmitted } from '~/posthogs/events';
import {
  Button,
  InputSelectField,
  InputNumberField,
  Radio,
  RadioGroup,
  Typography,
} from '@tempmee/react';
import { colors } from '@tempmee/tailwind';

import { renderIntroductionLine } from '../../../commonComponents/RenderListFunc';
import { createLoadingSelector } from '../../../api/selectors';
import { getShouldOfficeShowEfda } from '../../../growthbook';
import { getAllProfessionTypeOptions } from '../../../utils/DentistUtils';
import { UserProfession, UserSubProfession } from '../../../enums/UserProfession';
import { EnumHelper } from '../../../utils/EnumHelper';
import { Box } from '~/commonComponents';
import { SurveyPopup } from './components/SurveyPopup';
import {
  FormErrorBanner,
  FormActions,
  useScrollToError,
  TextAreaField,
} from '~/components/shared/forms';

interface IPPViewProps {
  btnSubmitClicked: (jobData: any) => void;
}

export default function PPView({ btnSubmitClicked }: IPPViewProps) {
  const [date, setDate] = useState('ASAP');
  const [description, setDescription] = useState('');
  const [schedule, setSchedule] = useState('Full Time Preferred');
  const [rateFrom, setRateFrom] = useState('');
  const [rateTo, setRateTo] = useState('');
  const [benefits, setBenefits] = useState('Full Benefits');
  const [interviewInterested, setInterviewInterested] = useState<boolean | null>(null);
  const [showSurveyPopup, setShowSurveyPopup] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const loadingSelector = createLoadingSelector(['POST_PP_JOB']);
  const isLoading = useAppSelector((state) => loadingSelector(state));

  const [professionType, setProfessionType] = useState('RDH');

  // use profession. normalized sub-profession to profession parent
  const professionTypeNormalizedCode = UserProfession.getNormalizedCode(professionType);

  const allProfessions = useAppSelector((state) => state.user.allProfessions);
  const showEfda = getShouldOfficeShowEfda();
  const allProfessionOptions = getAllProfessionTypeOptions(allProfessions, { showEfda } as any);

  const isValidData =
    description !== '' && rateFrom !== '' && rateTo !== '' && interviewInterested != null;
  const isValidPayRange = Number(rateTo) >= Number(rateFrom);
  const buttonDisabled = !isValidData || !isValidPayRange;

  const showBanner = hasAttemptedSubmit && buttonDisabled;
  const bannerRef = useScrollToError(showBanner);

  const submitHasBeenPressed = () => {
    setHasAttemptedSubmit(true);

    if (buttonDisabled) {
      return;
    }

    if (!interviewInterested) {
      setShowSurveyPopup(true);
      return;
    }

    submitForm();
  };

  const submitForm = (reason?: string[]) => {
    const rate = `$${rateFrom} - $${rateTo}/hour`;
    const data = {
      start_date: date,
      content: description,
      position: schedule,
      rate,
      benefits,
      // use profession. normalized if sub-profession is included
      profession: professionTypeNormalizedCode,

      // include sub-profession if it's a member of UserSubProfession
      subProfession: EnumHelper.getObjectKeys(UserSubProfession).includes(professionType)
        ? professionType
        : null,

      workingInterview: {
        interested: !!interviewInterested,
        reason,
      },
    };

    firePermanentHireRequestSubmitted({
      professionType,
      startDate: date,
      scheduleType: schedule,
    });

    btnSubmitClicked(data);
  };

  return (
    <>
      <div className="card">
        <div style={{ margin: 30, maxWidth: 1100 }}>
          <div style={{ display: 'flex' }}>
            <div>
              <FormErrorBanner ref={bannerRef} visible={showBanner} />

              <h3
                style={{
                  fontFamily: 'Nunito',
                  color: colors.primary[500],
                }}
              >
                Request Permanent Hire
              </h3>

              <div>
                <p style={styles.titleStyle}>Profession Type:</p>

                <InputSelectField
                  value={professionType}
                  onChange={setProfessionType}
                  options={
                    allProfessionOptions?.map((profession) => ({
                      label: profession?.name,
                      value: profession?.code,
                    })) ?? []
                  }
                />
              </div>
              <div>
                <p style={styles.titleStyle}>Start Date:</p>

                <InputSelectField
                  value={date}
                  onChange={setDate}
                  options={[
                    { label: 'ASAP', value: 'ASAP' },
                    { label: '1-2 Weeks', value: '1-2 Weeks' },
                    { label: '2-4 Weeks', value: '2-4 Weeks' },
                    { label: '1-2 Months', value: '1-2 Months' },
                    { label: '3+ Months', value: '3+ Months' },
                  ]}
                />
              </div>
              <div>
                <p style={styles.titleStyle}>Schedule Type:</p>

                <InputSelectField
                  value={schedule}
                  onChange={setSchedule}
                  options={[
                    { label: 'Full Time Preferred', value: 'Full Time Preferred' },
                    { label: 'Part Time Preferred', value: 'Part Time Preferred' },
                    {
                      label: 'Flexible (Full or Part Time)',
                      value: 'Flexible (Full or Part Time)',
                    },
                  ]}
                />
              </div>
              <div>
                <p style={styles.titleStyle}>Pay Range:</p>
                <div
                  style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8 }}
                >
                  <InputNumberField
                    data-testid="rate-from-field"
                    isError={hasAttemptedSubmit && rateFrom === ''}
                    leftIcon={<span style={styles.rateIconStyle}>$</span>}
                    value={rateFrom}
                    min={0}
                    onChange={(e) => setRateFrom(e.target.value.replaceAll('-', ''))}
                  />
                  <p style={styles.rateStyle}>–</p>
                  <InputNumberField
                    data-testid="rate-to-field"
                    isError={hasAttemptedSubmit && (rateTo === '' || !isValidPayRange)}
                    leftIcon={<span style={styles.rateIconStyle}>$</span>}
                    value={rateTo}
                    min={0}
                    onChange={(e) => setRateTo(e.target.value.replaceAll('-', ''))}
                  />
                  <p style={styles.rateStyle}>/hr</p>
                </div>
              </div>

              <div>
                <p style={styles.titleStyle}>Benefit Package:</p>

                <InputSelectField
                  value={benefits}
                  onChange={setBenefits}
                  options={[
                    { label: 'Full Benefits', value: 'Full Benefits' },
                    { label: 'Some Benefits', value: 'Some Benefits' },
                    { label: 'No Benefits', value: 'No Benefits' },
                    { label: 'Negotiable Benefit', value: 'Negotiable Benefit' },
                  ]}
                />
              </div>

              <div>
                <p style={styles.titleStyle}>Job Description: </p>
                <TextAreaField
                  data-testid="description-field"
                  isError={hasAttemptedSubmit && description === ''}
                  style={{ width: '100%', fontFamily: 'Nunito' }}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>

              <Box marginTop={29} marginBottom={40}>
                <Typography as="span" bold>
                  Are you interested in setting up working interviews through GoTu?
                  <Typography as="span" color={colors.neutral[500]} style={{ fontWeight: 400 }}>
                    {' '}
                    (Required)
                  </Typography>
                  <Typography as="span" bold color={colors.error[500]}>
                    *
                  </Typography>
                </Typography>

                <RadioGroup
                  data-testid="interview-radio-group"
                  value={
                    interviewInterested === true ? 'yes' : interviewInterested === false ? 'no' : ''
                  }
                  onChange={(v) => setInterviewInterested(v === 'yes')}
                  error={hasAttemptedSubmit && interviewInterested === null}
                  style={{ marginTop: 18 }}
                >
                  <Radio value="yes" label="Yes" />
                  <Radio value="no" label="No" />
                </RadioGroup>
              </Box>

              <FormActions>
                <Button
                  data-testid="submit-button"
                  data-cy="submit-button"
                  isLoading={isLoading}
                  onClick={submitHasBeenPressed}
                >
                  Submit
                </Button>
              </FormActions>
            </div>
            <div
              style={{
                backgroundColor: colors.primary[50],
                padding: '10px 20px',
                borderRadius: 20,
                height: 260,
                marginLeft: '10%',
              }}
            >
              <h3
                style={{
                  fontFamily: 'Nunito',
                  color: colors.primary[500],
                }}
              >
                Recruiting Services Include:
              </h3>

              {renderIntroductionLine('A professional Recruiter')}
              {renderIntroductionLine('Reach ~95% of Dental Professionals')}
              {renderIntroductionLine('30 Day Great Hire Warranty')}
              {renderIntroductionLine('High-End Recruiter at Job Board Pricing')}
            </div>
          </div>
        </div>
      </div>

      {showSurveyPopup && <SurveyPopup onSkipPress={submitForm} onSubmitPress={submitForm} />}
    </>
  );
}
const styles = {
  cardBodyStyle: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    alignItems: 'center',
  },
  rateStyle: {
    fontSize: 16,
    fontFamily: 'Nunito',
    color: colors.neutral[600],
    fontWeight: 'bold' as const,
    margin: 0,
  },
  rateIconStyle: {
    fontSize: 16,
    fontFamily: 'Nunito',
    color: colors.neutral[600],
    fontWeight: 'bold' as const,
  },
  titleStyle: {
    fontSize: 16,
    fontFamily: 'Nunito',
    color: colors.neutral[600],
    fontWeight: 'bold',
    marginBottom: 10,
  },
};
